import { useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  format,
  isAfter,
  isSameDay,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayFlag, UI } from "react-day-picker";

const PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

const dayPickerClassNames = {
  [UI.Root]: "w-full",
  [UI.Months]: "w-full",
  [UI.Month]: "w-full px-3 pb-3 pt-2",
  [UI.MonthCaption]: "hidden",
  [UI.CaptionLabel]: "hidden",
  [UI.Nav]: "hidden",
  [UI.PreviousMonthButton]: "hidden",
  [UI.NextMonthButton]: "hidden",
  [UI.Chevron]: "h-0 w-0",
  [UI.MonthGrid]: "w-full table-fixed border-collapse",
  [UI.Weekdays]: "border-0",
  [UI.Weekday]:
    "pb-2 text-center text-[10.5px] font-semibold uppercase tracking-[0.06em] text-(--text-muted)",
  [UI.Week]: "h-8",
  [UI.Day]: "h-8 p-0 text-center align-middle",
  [UI.DayButton]:
    "mx-auto flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent p-0 text-[12.5px] font-medium text-(--text-primary) transition hover:bg-(--surface-2)",
  [DayFlag.outside]: "[&>button]:text-(--text-faint)",
  [DayFlag.today]: "[&>button]:font-bold [&>button]:text-(--brand-primary)",
};

const modifierClassNames = {
  selected_single:
    "[&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_start:
    "rounded-l-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_middle: "bg-(--pastel-violet) [&>button]:text-(--text-primary)",
  selected_end:
    "rounded-r-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
};

const presetRange = (days) => {
  const to = startOfDay(new Date());
  const from = startOfDay(subDays(to, days - 1));
  return { from, to };
};

const matchPreset = (range) => {
  for (const preset of PRESETS) {
    const candidate = presetRange(preset.days);
    if (
      isSameDay(candidate.from, range.from) &&
      isSameDay(candidate.to, range.to)
    ) {
      return preset;
    }
  }
  return null;
};

const fmtLabel = (range) => {
  if (isSameDay(range.from, range.to)) {
    return format(range.from, "MMM d, yyyy");
  }
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  return sameYear
    ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`
    : `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`;
};

export default function DateRangePicker({ value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(
    startOfMonth(value.to ?? new Date()),
  );
  const rootRef = useRef(null);

  const matchedPreset = useMemo(() => matchPreset(value), [value]);
  const triggerLabel = matchedPreset ? matchedPreset.label : fmtLabel(value);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setVisibleMonth(startOfMonth(value.to ?? new Date()));
      setAnchor(null);
    }
    prevOpenRef.current = open;
  }, [open, value.to]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const applyPreset = (preset) => {
    onChange(presetRange(preset.days));
    setOpen(false);
  };

  const handleDayClick = (day) => {
    const startOfClicked = startOfDay(day);
    if (anchor === null) {
      setAnchor(startOfClicked);
      onChange({ from: startOfClicked, to: startOfClicked });
      return;
    }
    const next = isAfter(anchor, startOfClicked)
      ? { from: startOfClicked, to: anchor }
      : { from: anchor, to: startOfClicked };
    onChange(next);
    setAnchor(null);
    setOpen(false);
  };

  const isSingleDate = isSameDay(value.from, value.to);
  const modifiers = isSingleDate
    ? { selected_single: value.from }
    : {
        selected_start: value.from,
        selected_middle: { after: value.from, before: value.to },
        selected_end: value.to,
      };

  return (
    <div ref={rootRef} className={`relative ${className}`.trim()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex items-center gap-2 rounded-(--radius-md) border bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors ${
          open
            ? "border-(--brand-primary)"
            : "border-(--border-strong) hover:border-(--brand-primary)"
        }`}
      >
        <Calendar
          size={15}
          strokeWidth={1.8}
          className="text-(--text-secondary)"
        />
        <span>{triggerLabel}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 z-30 mt-2 flex w-[460px] flex-col overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-1) sm:flex-row"
        >
          <div className="flex shrink-0 flex-col gap-1 border-(--border-subtle) bg-(--surface-2) p-3 sm:w-[140px] sm:border-r">
            <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-(--text-muted)">
              Quick range
            </div>
            {PRESETS.map((preset) => {
              const active = matchedPreset?.id === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={`rounded-md border-0 bg-transparent px-2 py-[7px] text-left text-[13px] transition-colors ${
                    active
                      ? "bg-(--pastel-violet) font-bold text-(--brand-primary)"
                      : "font-semibold text-(--text-secondary) hover:bg-(--surface-1) hover:text-(--text-primary)"
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setVisibleMonth(startOfMonth(value.to ?? new Date()));
                setAnchor(null);
              }}
              className={`mt-1 rounded-md border-0 bg-transparent px-2 py-[7px] text-left text-[13px] transition-colors ${
                matchedPreset === null
                  ? "bg-(--pastel-violet) font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:bg-(--surface-1) hover:text-(--text-primary)"
              }`}
            >
              Custom range
            </button>
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between border-b border-(--border-subtle) px-3 py-2">
              <button
                type="button"
                onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
                aria-label="Previous month"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
              >
                <ChevronLeft size={15} strokeWidth={2} />
              </button>
              <p className="text-[13px] font-bold text-(--text-primary)">
                {format(visibleMonth, "MMMM yyyy")}
              </p>
              <button
                type="button"
                onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
                aria-label="Next month"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
              >
                <ChevronRight size={15} strokeWidth={2} />
              </button>
            </div>

            <DayPicker
              fixedWeeks
              showOutsideDays
              hideNavigation
              month={visibleMonth}
              onMonthChange={setVisibleMonth}
              onDayClick={handleDayClick}
              modifiers={modifiers}
              modifiersClassNames={modifierClassNames}
              classNames={dayPickerClassNames}
            />
          </div>
        </div>
      )}
    </div>
  );
}
